import React from "react";
import { Provider, useDispatch } from "react-redux";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { CssBaseline } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { Toaster } from "sonner";
import theme from "./theme/mui-theme";
import store from "./store";

import NavBar from "./components/NavBar";
import CartDrawer from "./components/CartDrawer";
import SiteFooter from "./components/SiteFooter";
import ProtectedRoute from "./components/ProtectedRoute";
import ErrorPage from "./pages/ErrorPage";
import NotFoundPage from "./pages/NotFoundPage";
import WhatsAppButton from "./components/WhatsAppButton";

import Landing from "./pages/Landing";
import PLP from "./pages/PLP";
import PDP from "./pages/PDP";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import OrderSuccess from "./pages/OrderSuccess";
import OrderTracking from "./pages/OrderTracking";
import Wishlist from "./pages/Wishlist";
import KidilanTraveller from "./pages/KidilanTraveller";
import Contact from "./pages/Contact";
import { fetchCategories } from "./store/slices/categorySlice";
import ScrollToTop from "./utils/ScrollToTop";

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error caught by boundary:", error, errorInfo);
  }

  resetError = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorPage error={this.state.error} resetError={this.resetError} />
      );
    }

    return this.props.children;
  }
}

const RoutedLayout = () => {
  const location = useLocation();
  const dispatch = useDispatch();

  React.useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  const isPdp =
    /^\/product\//.test(location.pathname) || location.pathname === "/wishlist";

  return (
    <>
      <NavBar />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/shop" element={<PLP />} />
        <Route path="/product/:id" element={<PDP />} />
        <Route path="/cart" element={<Cart />} />
        <Route
          path="/checkout"
          element={
            <ProtectedRoute>
              <Checkout />
            </ProtectedRoute>
          }
        />
        <Route
          path="/order-success"
          element={
            <ProtectedRoute requireOrderState={true}>
              <OrderSuccess />
            </ProtectedRoute>
          }
        />
        <Route path="/order-tracking" element={<OrderTracking />} />
        <Route path="/wishlist" element={<Wishlist />} />
        <Route path="/stories" element={<KidilanTraveller />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/error" element={<ErrorPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <SiteFooter />
      <CartDrawer />
      <WhatsAppButton />
    </>
  );
};

const App = () => {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <BrowserRouter>
          <ScrollToTop />
          <ErrorBoundary>
            <RoutedLayout />
          </ErrorBoundary>
        </BrowserRouter>
        <Toaster
          richColors
          closeButton
          position="top-right"
          toastOptions={{
            style: {
              background: "white",
              color: "#1e293b",
              border: "1px solid #e2e8f0",
            },
            success: {
              style: {
                background: "#f0fdf4",
                border: "1px solid #bbf7d0",
                color: "#166534",
              },
            },
            error: {
              style: {
                background: "#fef2f2",
                border: "1px solid #fecaca",
                color: "#dc2626",
              },
            },
            warning: {
              style: {
                background: "#fffbeb",
                border: "1px solid #fed7aa",
                color: "#d97706",
              },
            },
          }}
        />
      </ThemeProvider>
    </Provider>
  );
};

export default App;
